import { Box, Button, CircularProgress, IconButton, Link, Typography } from "@mui/material";
import { ReactNode, useState } from "react";
import {
  AttachMoneyOutlined,
  BusinessOutlined,
  CancelOutlined,
  EmailOutlined,
  Inventory2Outlined,
  LocalShippingOutlined,
  OpenInNew,
  ReplayCircleFilled,
  VolunteerActivismOutlined,
} from "@mui/icons-material";
import {
  CarrierLogo,
  formatCarrierLogoCarrier,
} from "@features/orders-features/components/order-detail-panel/CarrierLogo";
import { resolveCarrierTrackUrl } from "@features/orders-features/components/order-detail-panel/constants";
import type { ShipmentGroup } from "@features/orders-features/components/order-detail-panel/ShipmentMeta";
import { formatOrderDate } from "@features/orders-features/functions/formatOrderDate";
import { resolveShipmentDisplay } from "@features/orders-features/functions/shipmentDisplay";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { useOrderTelemetrySection } from "../../orderTelemetrySection";

type StatusKind =
  | "processing"
  | "shipped"
  | "delivered"
  | "email"
  | "services"
  | "fees"
  | "cancelled"
  | "returned";

interface StatusConfig {
  title: string;
  subtext: string;
  icon: ReactNode;
  /** Green ring fill percentage (0 = grey ring only). */
  progress: number;
}

// NOTE: MUI has no literal "filing cabinet" icon -- Inventory2Outlined is the
// closest match. The delivered icon interprets "orgnation" as organization
// (BusinessOutlined). Swap these here if different glyphs are preferred.
const STATUS_CONFIG: Record<StatusKind, StatusConfig> = {
  processing: {
    title: "Processing",
    subtext: "We're working on your products.",
    icon: <Inventory2Outlined fontSize="small" />,
    progress: 0,
  },
  shipped: {
    title: "Shipped",
    subtext: "",
    icon: <LocalShippingOutlined fontSize="small" />,
    progress: 50,
  },
  delivered: {
    title: "Delivered",
    subtext: "",
    icon: <BusinessOutlined fontSize="small" />,
    progress: 100,
  },
  email: {
    title: "Email Delivery",
    subtext:
      "Expect an email from your account team containing product details and licenses.",
    icon: <EmailOutlined fontSize="small" />,
    progress: 100,
  },
  services: {
    title: "Services",
    subtext: "View upcoming or past services rendered.",
    icon: <VolunteerActivismOutlined fontSize="small" />,
    progress: 100,
  },
  fees: {
    title: "Fees",
    subtext:
      "View handling, processing, or warehousing fees associated with your order.",
    icon: <AttachMoneyOutlined fontSize="small" />,
    progress: 100,
  },
  cancelled: {
    title: "Cancelled",
    subtext: "These items were cancelled.",
    icon: <CancelOutlined fontSize="small" />,
    progress: 0,
  },
  returned: {
    title: "Returned",
    subtext: "These items were returned.",
    icon: <ReplayCircleFilled fontSize="small" />,
    progress: 0,
  },
};

function resolveStatusKind(title: string): StatusKind {
  const value = title.toLowerCase();
  if (value.includes("delivered")) return "delivered";
  if (value.includes("shipped") || value.includes("shipping")) return "shipped";
  if (value.includes("email")) return "email";
  if (value.includes("service")) return "services";
  if (value.includes("fee")) return "fees";
  if (value.includes("cancel")) return "cancelled";
  if (value.includes("return")) return "returned";
  return "processing";
}

const CIRCLE_SIZE = 44;
const CIRCLE_THICKNESS = 3.5;

function StatusCircle({
  icon,
  progress,
}: Readonly<{ icon: ReactNode; progress: number }>) {
  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        flexShrink: 0,
        // Icon stays a static muted grey; only the ring changes color.
        color: "text.secondary",
      }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={CIRCLE_SIZE}
        thickness={CIRCLE_THICKNESS}
        sx={{ color: "action.hover" }}
      />
      {progress > 0 && (
        <CircularProgress
          variant="determinate"
          value={progress}
          size={CIRCLE_SIZE}
          thickness={CIRCLE_THICKNESS}
          sx={{ color: "success.main", position: "absolute", left: 0 }}
        />
      )}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "inherit"
        }}
      >
        {icon}
      </Box>
    </Box>
  );
}

function ShipmentTrackingSubtext({
  group,
  shipmentLabel,
}: Readonly<{ group?: ShipmentGroup; shipmentLabel?: string }>) {
  if (!group) return null;

  const hasTracking = !!group.trackingNumber;
  const trackUrl = resolveCarrierTrackUrl(group.carrierEnum, group.trackingNumber);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
        mt: 0.25,
      }}
    >
      {shipmentLabel && (
        <Typography
          variant="body2"
          color="text.secondary"
        >
          {shipmentLabel}
        </Typography>
      )}
      {group.shippedOn && (
        <Typography variant="body2" color="text.secondary">
          Shipped {formatOrderDate(group.shippedOn)}
        </Typography>
      )}
      {hasTracking && (
        <Typography variant="body2" color="text.secondary">
          Tracking #:{" "}
          <Link
            href={trackUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              verticalAlign: "bottom",
            }}
          >
            {group.trackingNumber}
            <OpenInNew fontSize="inherit" color="primary" />
          </Link>
        </Typography>
      )}
      {hasTracking && (
        <CarrierLogo
          carrier={formatCarrierLogoCarrier(group.carrierEnum)}
          carrierName={group.carrier}
        />
      )}
    </Box>
  );
}

interface ShipmentStatusHeaderProps {
  sectionTitle: string;
  shipmentLabel?: string;
  group?: ShipmentGroup;
  orderDate?: Date | string | null;
  onTrackPackage?: () => void;
}

function TrackPackageButton({
  onTrackPackage,
  trackUrl,
  telemetrySection,
}: Readonly<{
  onTrackPackage?: () => void;
  trackUrl: string;
  telemetrySection: string;
}>) {
  const buttonSx = { ml: "auto", fontSize: 12, letterSpacing: 0.5 } as const;
  const telemetry = telemetryProps({
    name: "orders_track_package",
    section: telemetrySection,
    label: "Track package",
  });

  if (onTrackPackage) {
    return (
      <Button
        size="small"
        color="inherit"
        variant="outlined"
        onClick={onTrackPackage}
        sx={buttonSx}
        {...telemetry}
      >
        TRACK PACKAGE
      </Button>
    );
  }

  return (
    <Button
      size="small"
      color="inherit"
      variant="outlined"
      href={trackUrl}
      target="_blank"
      rel="noopener noreferrer"
      sx={buttonSx}
      {...telemetry}
    >
      TRACK PACKAGE
    </Button>
  );
}


export function ShipmentStatusHeader({
  sectionTitle,
  shipmentLabel,
  group,
  orderDate,
  onTrackPackage,
}: Readonly<ShipmentStatusHeaderProps>) {
  const telemetrySection = useOrderTelemetrySection();
  const kind = resolveStatusKind(sectionTitle);
  const config = STATUS_CONFIG[kind];
  const isShipping = kind === "shipped" || kind === "delivered";

  const hasTracking = !!group?.trackingNumber;
  const trackUrl = resolveCarrierTrackUrl(group?.carrierEnum, group?.trackingNumber);

  // Use the exact same status/date text as the shipment meta on the detail panel.
  const display = resolveShipmentDisplay({
    carrierEnum: group?.carrierEnum,
    status: sectionTitle,
    deliveryOn: group?.deliveryOn,
    estimatedDeliveryOn: group?.estimatedDeliveryOn,
    orderDate,
  });

  const primaryText = isShipping ? display.statusText : config.title;

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      <StatusCircle icon={config.icon} progress={config.progress} />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
            {primaryText}
          </Typography>

          {isShipping && display.detailText && (
            <Typography variant="body2" color="text.secondary">
              {display.detailText}
            </Typography>
          )}

          {isShipping && hasTracking && (
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
              <TrackPackageButton
                onTrackPackage={onTrackPackage}
                trackUrl={trackUrl}
                telemetrySection={telemetrySection}
              />
            </Box>
          )}
        </Box>

        {isShipping ? (
          <ShipmentTrackingSubtext group={group} shipmentLabel={shipmentLabel} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {config.subtext}
          </Typography>
        )}
      </Box>
    </Box>
  );
}








